import { Injectable }       from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

import { ApiService }                       from '../api/api.service';
import { CodeTypeModel }                    from '../core/models/code-type';
import { CompanyMessagesModel }             from '../companies/models/company.messages';
import { CompanyService }                   from '../companies/company.service';
import { StatementsCustomerModel }          from './typings/statements.customer';
import { StatementsFiltersModel }           from './typings/statements.filters';
import { StatementsSeparateCustomersModel } from './typings/statements.separate-customers';
import { TableQueryParameters }             from '../table/types/table-query-parameters';

@Injectable({
  providedIn: 'root'
})
export class StatementsService {
  public broadcastKey  = 'filterStatements';
  public tableCacheKey = 'statements';

  constructor (
    private apiService      : ApiService,
    private companyService  : CompanyService,
    private translateService: TranslateService
  ) {}

  email ( separateCustomers : StatementsSeparateCustomersModel, filters = {} as StatementsFiltersModel ) : Promise<StatementsSeparateCustomersModel> {
    const settings = filters.statement
      ? {
          email    : separateCustomers.email,
          as_at    : filters.asAt,
          statement: filters.statement
        }
      : {
          email       : separateCustomers.email,
          begin_date  : filters.beginDate,
          end_date    : filters.endDate,
          include_paid: filters.includePaid,
          statement   : filters.statement
        };

    return this.companyService.getDefaultMessages()
      .then(( companyMessages : CompanyMessagesModel ) => this.apiService.post('/reports/jasper_statements_bulk', {
        email: {
          body   : companyMessages.statement_email_message,
          subject: `${
            this.translateService.instant('MENU_SPACE.STATEMENT')
          } - ${
            this.companyService.getName()
          }`
        },
        settings
      }))
      .then(() => separateCustomers);
  }

  getConstraintsTypes () : Promise<Array<CodeTypeModel>> {
    return this.apiService.get('/system/statement_constraint_types');
  }

  getCustomersList ( filters = {} as StatementsFiltersModel, params : TableQueryParameters = {} ) : Promise<Array<StatementsCustomerModel>> {
    return this.apiService.get('/customers/customer_list_for_statements', [
        filters.beginDate      || '*',
        filters.endDate        || '*',
        filters.includePaid    || false,
        filters.asAt           || '*',
        filters.statement      || false,
        params.field           || 'customer.display_name',
        params.direction       || 'asc',
        filters.constraintType || '*',
        params.offset          || 0,
        params.pageSize        || 10,
      ])
      .catch(() => []);
  }

  getCustomersListCount ( filters = {} as StatementsFiltersModel ) : Promise<number> {
    return this.apiService.get('/customers/customer_list_for_statements_count', [
        filters.beginDate      || '*',
        filters.endDate        || '*',
        filters.includePaid    || false,
        filters.asAt           || '*',
        filters.statement      || 'false',
        filters.constraintType || '*'
      ]);
  }

  print ( separateCustomers : StatementsSeparateCustomersModel, filters = {} as StatementsFiltersModel ) : Promise<string> {
    const params = filters.statement
      ? {
          print    : separateCustomers.email,
          as_at    : filters.asAt,
          statement: filters.statement
        }
      : {
          print       : separateCustomers.email,
          begin_date  : filters.beginDate,
          end_date    : filters.endDate,
          include_paid: filters.includePaid,
          statement   : filters.statement
        };

    const url = filters.statement
      ? '/reports/jasper_statements_306090_print'
      : '/reports/jasper_statements_print';

    return this.apiService.post(url, params, 'print', null, {
        responseType: 'arraybuffer'
      })
      .then(response => {
        const file = new Blob([ response ], { type: 'application/pdf' });

        return URL.createObjectURL(file);
      });
  }

  separate ( constraintType : string, customers : Array<StatementsCustomerModel> ) : Promise<StatementsSeparateCustomersModel> {
    return this.apiService.post('/customers/statements_separate', {
        constraint_type: constraintType,
        customers      : customers
      }, 'customers');
  }
}
